import { TestCategory } from "types/tests/core";
import {
  CPTData,
  DotEnumerationResult,
  DyscalculiaResultItem,
  DyscalculiaTaskResult,
  MagnitudeComparisonResult,
  OcularMotilityMetrics,
  ReadingAssessmentMetricSummary,
  StimulusResponseState,
  StroopResult,
} from "types/tests/specifics";
import { findBand } from "./normativeBands";
import { bandStatusForScore, scoreMetric } from "./scoreMetric";
import { PerTestDetail, TEST_CATEGORY_TO_AXIS } from "./types";

export interface AggregateInput {
  category: TestCategory;
  test: TestDocumentLike | undefined;
  age: number;
}

export interface TestDocumentLike {
  results?: unknown;
  taskResults?: unknown;
  wcstData?: unknown;
}

interface WcstTrialDocument {
  selectionTime?: number;
  correctness?: boolean;
  ruleType?: string;
  trialNumber?: number;
}

const average = (values: number[]): number | null => {
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
};

const scoreOrNull = (
  category: TestCategory,
  metric: string,
  value: number | null | undefined,
  age: number,
): number | null => {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const band = findBand(category, metric, age);
  if (!band) return null;
  return scoreMetric(value, band);
};

function aggregateWcst(
  test: TestDocumentLike | undefined,
  age: number,
): { score: number | null; metricValues: Record<string, number | null> } {
  const trials: WcstTrialDocument[] = Array.isArray(test?.results)
    ? (test?.results as WcstTrialDocument[])
    : [];

  if (trials.length === 0) {
    return { score: null, metricValues: { accuracy: null, averageSelectionTimeSeconds: null } };
  }

  const correctnessValues = trials
    .map((t) => t.correctness)
    .filter((v): v is boolean => typeof v === "boolean");
  const accuracy =
    correctnessValues.length === 0
      ? null
      : correctnessValues.filter((v) => v).length / correctnessValues.length;

  const selectionTimes = trials
    .map((t) => t.selectionTime)
    .filter((v): v is number => typeof v === "number");
  const avgSelectionTime = average(selectionTimes);

  const accuracyScore = scoreOrNull(TestCategory.WisconsinCardSorting, "accuracy", accuracy, age);
  const speedScore = scoreOrNull(
    TestCategory.WisconsinCardSorting,
    "averageSelectionTimeSeconds",
    avgSelectionTime,
    age,
  );
  const scores = [accuracyScore, speedScore].filter((s): s is number => s !== null);
  return {
    score: scores.length === 0 ? null : average(scores),
    metricValues: { accuracy, averageSelectionTimeSeconds: avgSelectionTime },
  };
}

function aggregateReadingAssessment(
  summary: ReadingAssessmentMetricSummary | undefined,
  age: number,
): { score: number | null; metricValues: Record<string, number | null> } {
  const wpm = summary?.wordsPerMinute ?? null;
  const excessivePct = summary?.excessiveFixationsPercentage ?? null;
  const wpmScore = scoreOrNull(TestCategory.ReadingAssessment, "wordsPerMinute", wpm, age);
  const excessiveScore = scoreOrNull(
    TestCategory.ReadingAssessment,
    "excessiveFixationsPercentage",
    excessivePct,
    age,
  );
  const scores = [wpmScore, excessiveScore].filter((s): s is number => s !== null);
  return {
    score: scores.length === 0 ? null : average(scores),
    metricValues: { wordsPerMinute: wpm, excessiveFixationsPercentage: excessivePct },
  };
}

const isMagnitudeOrStroopResult = (
  item: DyscalculiaResultItem,
): item is MagnitudeComparisonResult | StroopResult =>
  "wasCorrect" in item && "reactionTimeSeconds" in item;

const isDotEnumerationResult = (item: DyscalculiaResultItem): item is DotEnumerationResult =>
  "wasCorrect" in item && "selectedValue" in item;

function aggregateDyscalculia(
  test: TestDocumentLike | undefined,
  age: number,
): { score: number | null; metricValues: Record<string, number | null> } {
  const tasks: DyscalculiaTaskResult[] = Array.isArray(test?.taskResults)
    ? (test?.taskResults as DyscalculiaTaskResult[])
    : [];
  const allResults: DyscalculiaResultItem[] = tasks.flatMap((t) => t.results ?? []);

  const considered = allResults.filter(
    (r) => isMagnitudeOrStroopResult(r) || isDotEnumerationResult(r),
  );
  const accuracy =
    considered.length === 0
      ? null
      : considered.filter((r) => r.wasCorrect === true).length / considered.length;

  const reactionTimes = allResults
    .map((r) => (r as MagnitudeComparisonResult).reactionTimeSeconds)
    .filter((rt): rt is number => typeof rt === "number");
  const avgReaction = average(reactionTimes);

  const accuracyScore = scoreOrNull(TestCategory.Dyscalculia, "accuracy", accuracy, age);
  const reactionScore = scoreOrNull(
    TestCategory.Dyscalculia,
    "averageReactionTimeSeconds",
    avgReaction,
    age,
  );
  const scores = [accuracyScore, reactionScore].filter((s): s is number => s !== null);
  return {
    score: scores.length === 0 ? null : average(scores),
    metricValues: { accuracy, averageReactionTimeSeconds: avgReaction },
  };
}

function aggregateContinuousPerformance(data: CPTData | undefined): {
  score: number | null;
  metricValues: Record<string, number | null>;
} {
  const responses = data?.responses ?? [];
  if (responses.length === 0) {
    return { score: null, metricValues: { hitRate: null, inhibition: null } };
  }

  const correct = responses.filter((r) => r.state === StimulusResponseState.Correct).length;
  const misses = responses.filter((r) => r.state === StimulusResponseState.Miss).length;
  const falsePositives = responses.filter((r) =>
    r.state.toString().startsWith("falsePositive"),
  ).length;

  const targetAttempts = correct + misses;
  const hitRate = targetAttempts === 0 ? null : (correct / targetAttempts) * 100;
  const totalActions = correct + falsePositives;
  const inhibition = totalActions === 0 ? null : (correct / totalActions) * 100;

  const scores = [hitRate, inhibition].filter((s): s is number => s !== null);
  return {
    score: scores.length === 0 ? null : average(scores),
    metricValues: { hitRate, inhibition },
  };
}

function aggregateOcularMotility(
  metrics: Partial<OcularMotilityMetrics> | undefined,
  age: number,
): { score: number | null; metricValues: Record<string, number | null> } {
  const pursuitGains = [metrics?.leftPursuitGain, metrics?.rightPursuitGain].filter(
    (v): v is number => typeof v === "number",
  );
  const meanErrors = [metrics?.leftMeanError, metrics?.rightMeanError].filter(
    (v): v is number => typeof v === "number",
  );

  const avgPursuitGain = average(pursuitGains);
  const avgMeanError = average(meanErrors);

  const pursuitScore = scoreOrNull(TestCategory.OcularMotility, "pursuitGain", avgPursuitGain, age);
  const errorScore = scoreOrNull(TestCategory.OcularMotility, "meanError", avgMeanError, age);
  const scores = [pursuitScore, errorScore].filter((s): s is number => s !== null);
  return {
    score: scores.length === 0 ? null : average(scores),
    metricValues: { pursuitGain: avgPursuitGain, meanError: avgMeanError },
  };
}

export function aggregateAxis(input: AggregateInput): PerTestDetail | null {
  const axis = TEST_CATEGORY_TO_AXIS[input.category];
  if (!axis) {
    // The category is not part of the cognitive profile (no axis mapping).
    return null;
  }

  let score: number | null = null;
  let metricValues: Record<string, number | null> = {};

  switch (input.category) {
    case TestCategory.WisconsinCardSorting: {
      const result = aggregateWcst(input.test, input.age);
      score = result.score;
      metricValues = result.metricValues;
      break;
    }
    case TestCategory.ReadingAssessment: {
      const summary = (
        input.test?.results as { metrics?: { summary?: ReadingAssessmentMetricSummary } }
      )?.metrics?.summary;
      const result = aggregateReadingAssessment(summary, input.age);
      score = result.score;
      metricValues = result.metricValues;
      break;
    }
    case TestCategory.Dyscalculia: {
      const result = aggregateDyscalculia(input.test, input.age);
      score = result.score;
      metricValues = result.metricValues;
      break;
    }
    case TestCategory.ContinuousPerformance: {
      const result = aggregateContinuousPerformance(input.test?.results as CPTData | undefined);
      score = result.score;
      metricValues = result.metricValues;
      break;
    }
    case TestCategory.OcularMotility: {
      const result = aggregateOcularMotility(
        input.test?.results as Partial<OcularMotilityMetrics> | undefined,
        input.age,
      );
      score = result.score;
      metricValues = result.metricValues;
      break;
    }
  }

  return {
    category: input.category,
    axis,
    score,
    status: bandStatusForScore(score),
    metricValues,
  };
}
